import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { printPlugin } from '@react-pdf-viewer/print';
import { searchPlugin } from '@react-pdf-viewer/search';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { Box, IconButton, Typography, Paper, Tooltip, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getApiUrl } from '../../config/appConfig';

// Importar estilos
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

interface PDFViewerProps {
  pdfUrl: string;
  onClose: () => void;
  title: string;
  documentId: string;
  documentType?:
    | 'recibo'
    | 'pedido'
    | 'cotizacion'
    | 'ajuste'
    | 'factura'
    | 'ordenPago'
    | 'nota-credito'
    | 'nota-debito'
    | 'remito'
    | 'transferenciaCaja'
    | 'cierreCaja'
    | 'cierreCajaIngresoEgreso'
    | 'cierreCajaPorTipoPago'
    | 'ordenCompra'
    | 'devolucion'
    /** PDF generado en el cliente (listados, etc.); sin URL pública de API */
    | 'informeCliente';
  downloadFileName?: string;
  onPrint?: () => void;
  onDownload?: () => void;
}

/** PDF generado en el navegador (blob:) no tiene URL pública para compartir por WhatsApp. */
const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfUrl,
  onClose,
  title,
  documentId,
  documentType = 'recibo',
  downloadFileName,
  onPrint,
  onDownload,
}) => {
  const theme = useTheme();
  const overlayZ = theme.zIndex.modal + 2;
  const backdropZ = theme.zIndex.modal + 1;
  const isBlobPreview = pdfUrl.startsWith('blob:');
  const printPluginInstance = printPlugin();
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const searchPluginInstance = searchPlugin();
  const zoomPluginInstance = zoomPlugin();

  useEffect(() => {
    document.documentElement.setAttribute('data-pdf-preview-open', '');
    return () => document.documentElement.removeAttribute('data-pdf-preview-open');
  }, []);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = downloadFileName || `${title}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    onDownload?.();
  };

  const handlePrint = () => {
    printPluginInstance.print();
    onPrint?.();
  };

  const handleShareWhatsApp = () => {
    // Determinar la URL pública según el tipo de documento
    const apiUrl = getApiUrl();
    let publicUrl = '';
    switch (documentType) {
      case 'informeCliente':
        publicUrl = '';
        break;
      case 'pedido':
        publicUrl = `${apiUrl}/api/pedidosventa/${documentId}/pdf`;
        break;
      case 'cotizacion':
        publicUrl = `${apiUrl}/api/CotizacionesVenta/${documentId}/pdf`;
        break;
      case 'ajuste':
        publicUrl = `${apiUrl}/api/ajustes/${documentId}/pdf`;
        break;
      case 'factura':
        publicUrl = `${apiUrl}/api/FacturasVenta/${documentId}/pdf`;
        break;
      case 'ordenPago':
        publicUrl = `${apiUrl}/api/tesoreria/orden-pago/${documentId}/pdf`;
        break;
      case 'nota-credito':
        publicUrl = `${apiUrl}/api/NotasCreditoVenta/${documentId}/pdf`;
        break;
      case 'nota-debito':
        publicUrl = `${apiUrl}/api/NotasDebitoVenta/${documentId}/pdf`;
        break;
      case 'remito':
        publicUrl = `${apiUrl}/api/RemitosVenta/${documentId}/pdf`;
        break;
      case 'transferenciaCaja':
        publicUrl = `${apiUrl}/api/TesoreriaCaja/transferencias-caja/${documentId}/pdf`;
        break;
      case 'cierreCaja':
        publicUrl = `${apiUrl}/api/Caja/${documentId}/cierre/pdf`;
        break;
      case 'cierreCajaIngresoEgreso':
        publicUrl = `${apiUrl}/api/Caja/${documentId}/cierre/pdf?formato=ingresoEgreso`;
        break;
      case 'cierreCajaPorTipoPago':
        publicUrl = `${apiUrl}/api/Caja/${documentId}/cierre/pdf`;
        break;
      case 'ordenCompra':
        publicUrl = `${apiUrl}/api/OrdenesCompra/${documentId}/pdf`;
        break;
      case 'devolucion':
        publicUrl = `${apiUrl}/api/DevolucionesVenta/${documentId}/pdf`;
        break;
      case 'recibo':
      default:
        publicUrl = `${apiUrl}/api/tesoreria/recibos/${documentId}/pdf`;
        break;
    }
    
    const message = encodeURIComponent(`Compartiendo ${title}\n\nPuedes descargar el documento aquí: ${publicUrl}`);
    const whatsappUrl = `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const viewer = (
    <>
      <Box
        className="pdf-viewer-backdrop"
        aria-hidden
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          zIndex: backdropZ,
          '@media print': { display: 'none' },
        }}
      />
      <Paper
        className="pdf-viewer-overlay"
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          height: '90%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          overflow: 'hidden',
          zIndex: overlayZ,
          '@media print': {
            position: 'static',
            transform: 'none',
            width: '100%',
            height: 'auto',
            maxHeight: 'none',
            boxShadow: 'none',
            p: 0,
            overflow: 'visible',
          },
        }}
      >
      <Box
        className="pdf-viewer-chrome"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          '@media print': { display: 'none' }
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <Box>
          {!isBlobPreview ? (
            <Tooltip title="Compartir por WhatsApp">
              <IconButton onClick={handleShareWhatsApp} color="success">
                <WhatsAppIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Imprimir (solo el PDF, sin cabecera de la aplicación)">
            <IconButton onClick={handlePrint} color="primary" aria-label="Imprimir PDF">
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cerrar">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          height: 'calc(100% - 64px)',
          overflow: 'hidden',
          '@media print': { height: 'auto', overflow: 'visible' }
        }}
      >
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <Viewer
            fileUrl={pdfUrl}
            plugins={[
              printPluginInstance,
              defaultLayoutPluginInstance,
              searchPluginInstance,
              zoomPluginInstance,
            ]}
            defaultScale={2}
            theme={{
              theme: 'light',
            }}
          />
        </Worker>
      </Box>
    </Paper>
    </>
  );

  return createPortal(viewer, document.body);
};

export default PDFViewer;
